import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import FinalFormColorPicker from 'components/final-form/ColorPicker';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithTextGetter,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import { CamelCaseCodeInput } from 'components/final-form/Inputs';
import Select from 'components/final-form/Select';
import TopRowEntityColorPicker from 'components/final-form/TopRowEntityColorPicker';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import {
  InterventionUpsertRefDataConsumerRenderProps,
  withInterventionUpsertRefDataContext
} from 'components/settings/intervention/InterventionUpsertRefDataContext';
import { FormState } from 'final-form';
import createPmedDecorator from 'final-form-calculate-pmed/decorator';
import React, { Component, Fragment } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Table } from 'semantic-ui-react';
import { addIntervention, editIntervention } from 'services/interventionServices';
import { getAllLabTestPanels } from 'services/labTestPanelService';
import { getAllShippableMedicaments } from 'services/medicamentServices';
import styled from 'styled-components';
import { CovidTestSampleMethod, InterventionExternalCode, InterventionType } from 'ts-types/api.enums';
import {
  BlockProcedureExtResponse,
  LabTestPanelDto,
  QuestionSetDto,
  TariffDto,
  UpsertInterventionDto
} from 'ts-types/api.types';
import { testAndCertificateExternalCodes } from 'utils/enumUtils';
import { errorUtils } from 'utils/errorUtils';
import { mustBeNumber, required, validateOnlyIfPresent } from 'utils/validatorUtils';

const UpsertInterventionFormContent = styled(FormContent)`
  //max-width: 570px;

  .field {
    margin-bottom: 0.75rem;
  }

  .divider {
    margin-bottom: 1rem;
  }

  .tariff-error {
    color: darkred;
  }

  .tariff-block-procedures {
    font-style: italic;
  }
`;

const StyledCheckBoxContainer = styled.div`
  display: inline-block;
  margin-bottom: 1em;
  margin-right: 2rem;
`

interface Props extends MessageSourceProps,
    AuthConsumerRenderProps,
    InterventionUpsertRefDataConsumerRenderProps {

  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  interventionId?: number,
  intervention?: UpsertInterventionDto,
  questionSets: QuestionSetDto[]
}

interface State {
  pageDataLoaded: boolean,
  interventionTypeOptions: DropdownOption[],
  shippableMedicamentOptions: DropdownOption[],
  externalCodeTestOptions: DropdownOption[],
  externalCodeVaccinationOptions: DropdownOption[],
  questionSetOptions: DropdownOption[],
  sampleMethodOptions: DropdownOption[],
  labTestPanels: LabTestPanelDto[],
  labTestPanelOptions: DropdownOption[],

  formValues: Partial<UpsertInterventionDto>,

  errorMessage?: string,
}

const oneBlockProcedurePerTariff = (
    tariffs: TariffDto[], blockProcedureMap: { [id: number]: BlockProcedureExtResponse }) => {

  return (value: any, allValues: any): any => {
    if (!value) {
      return undefined;
    }

    const selectedBlockProcedureIds: number[] = value;
    const selectedBlockProcedures = selectedBlockProcedureIds.map(id => blockProcedureMap[id]);
    const errorTariffs = tariffs.filter(tariff => {
      const blockProceduresForTariff = selectedBlockProcedures.filter(bp => bp.tariffs.includes(tariff.id));
      return blockProceduresForTariff.length > 1;
    });
    return errorTariffs.length > 1 ? "tariffWithMultipleBlockProcedures" : undefined;
  };
};

class UpsertInterventionForm extends Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  formDecorators = [
    createPmedDecorator({
      field: "type",
      updateOnPristine: false,
      updates: {
        externalCode: (type: any, allValues: any) => ""
      }
    })
  ];

  private initialValues: Partial<UpsertInterventionDto>;

  constructor(props: Props) {
    super(props);

    const {interventionId, intervention, getMessage} = this.props;

    const interventionTypeOptions = stringArrayToDropdownOptionArray(
        Object.values(InterventionType), getMessage, "enum.InterventionType");

    const externalTestCodes = testAndCertificateExternalCodes();
    const externalCodeTestOptions = stringArrayToDropdownOptionArray(
        externalTestCodes, getMessage, "enum.InterventionExternalCode");

    const externalVaccinationCodes = Object.values(InterventionExternalCode)
        .filter(code => InterventionExternalCode[code].startsWith("VACCINATION_"));
    const externalCodeVaccinationOptions = stringArrayToDropdownOptionArray(
        externalVaccinationCodes, getMessage, "enum.InterventionExternalCode");

    const sampleMethodOptions = stringArrayToDropdownOptionArray(
        Object.values(CovidTestSampleMethod), getMessage, "covidTestSampleMethod");

    this.initialValues = {
      authorityNotificationRequired: false,
      active: true,
      orderItem: {
        id: -1,
        name: "",
        code: "",
        keyPrefix: "interventionOrder",
        reportNamePrefix: "Intervention report",
        reportType: "INTERVENTION_ORDER_REPORT",
        panelIds: []
      }
    };
    if (interventionId) {
      this.initialValues = {
        ...intervention
      };
    }

    const questionSetOptions = props.questionSets.map(qs => {
      const nameMap: { [lang: string]: string } =
          {
            "de": qs.name,
            "en": qs.nameEn || qs.name,
            "fr": qs.nameFr || qs.name,
            "it": qs.nameIt || qs.name
          };
      return {
        key: qs.id,
        text: nameMap[props.language],
        value: qs.id
      }
    });

    this.state = {
      pageDataLoaded: false,
      interventionTypeOptions,
      externalCodeTestOptions,
      externalCodeVaccinationOptions,
      questionSetOptions: questionSetOptions,
      shippableMedicamentOptions: [],
      sampleMethodOptions,
      labTestPanels: [],
      labTestPanelOptions: [],

      formValues: {...this.initialValues}
    }

    this.loadPageData();
  }

  loadPageData = async () => {
    try {
      const medicamentDtos = await getAllShippableMedicaments(this.cancelTokenSource);

      const shippableMedicamentOptions = mapToDropdownOptionArrayWithTextGetter(
          medicamentDtos, item => `${item.code} - ${item.description}`);

      const labTestPanels = await getAllLabTestPanels(this.cancelTokenSource);
      const labTestPanelOptions = mapToDropdownOptionArray(
          labTestPanels, "name", "id", "id");

      this.setState({
        shippableMedicamentOptions,
        labTestPanels,
        labTestPanelOptions
      });
    } finally {
      this.setState({
        pageDataLoaded: true
      });
    }
  }

  handleChange = ({values}: FormState): void => {

  }

  handleSubmit = async (values: Partial<UpsertInterventionDto>) => {
    let saveValues: Partial<UpsertInterventionDto> = {...values};

    const isCovidTest = saveValues.type === InterventionType.COVID_TEST;
    const isCovidVaccination = saveValues.type === InterventionType.COVID_VACCINATION;
    if (isCovidTest || isCovidVaccination) {
      saveValues.blockProcedureIds = [];
    } else {
      saveValues.medicamentId = undefined;
      saveValues.externalCode = undefined;
    }

    const {interventionId, onSave} = this.props;
    const cancelTokenSource = this.cancelTokenSource;

    if (interventionId) {
      await editIntervention(interventionId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    } else {
      await addIntervention(saveValues, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.duplicateExternalCode
    ];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    if (!this.state.pageDataLoaded) {
      return <></>;
    }

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={this.initialValues}
            decorators={this.formDecorators}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting, values}: FormRenderProps): React.ReactNode => {

    const {tariffs, blockProcedures, blockProcedureMap, getMessage, onCancel} = this.props;
    const {
      interventionTypeOptions,
      externalCodeTestOptions,
      externalCodeVaccinationOptions,
      shippableMedicamentOptions,
      questionSetOptions,
      labTestPanelOptions,
      sampleMethodOptions,
      errorMessage
    } = this.state;

    const blockProcedureOptions = mapToDropdownOptionArray(blockProcedures, "description");

    const numberValidator = validateOnlyIfPresent(mustBeNumber);

    const selectedBlockProcedures: BlockProcedureExtResponse[] =
        values.blockProcedureIds ? values.blockProcedureIds.map((bp: number) => blockProcedureMap[bp]) : [];

    const isCovidTest = values.type === InterventionType.COVID_TEST;
    const isVaccination = values.type === InterventionType.COVID_VACCINATION;
    const isClinicalQuestions = values.externalCode === InterventionExternalCode.CLINICAL_QUESTIONS_TEST;

    let externalCodeOptions: DropdownOption[] = [...externalCodeTestOptions, ...externalCodeVaccinationOptions];
    if (isCovidTest) {
      externalCodeOptions = externalCodeTestOptions;
    } else if (isVaccination) {
      externalCodeOptions = externalCodeVaccinationOptions;
    }

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <UpsertInterventionFormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <div>
                      <StyledCheckBoxContainer>
                        <Field
                            name="active"
                            component={CheckBox}
                            label={getMessage("guarantor.active")}
                            toggle
                        />
                      </StyledCheckBoxContainer>

                      <TopRowEntityColorPicker>
                        <Field
                            id="color"
                            name="color"
                            component={FinalFormColorPicker}
                        />
                      </TopRowEntityColorPicker>
                    </div>

                    <label htmlFor="description">{getMessage("intervention.description")}</label>
                    <Field
                        fluid
                        id="description"
                        name="description"
                        component={Input}
                        validate={required}
                        autoFocus
                    />
                    <label htmlFor="descriptionEn">{getMessage("intervention.descriptionEn")}</label>
                    <Field
                        fluid
                        id="descriptionEn"
                        name="descriptionEn"
                        component={Input}
                    />
                    <label htmlFor="descriptionFr">{getMessage("intervention.descriptionFr")}</label>
                    <Field
                        fluid
                        id="descriptionFr"
                        name="descriptionFr"
                        component={Input}
                    />
                    <label htmlFor="descriptionIt">{getMessage("intervention.descriptionIt")}</label>
                    <Field
                        fluid
                        id="descriptionIt"
                        name="descriptionIt"
                        component={Input}
                    />

                    <label htmlFor="interventionType">{getMessage("intervention.interventionType")}</label>
                    <Field
                        label={`${getMessage("intervention.interventionType")}`}
                        id="type"
                        name="type"
                        component={Select}
                        options={interventionTypeOptions}
                        validate={required}
                        fluid
                    />

                    {
                        (isCovidTest || isVaccination) &&
                        <>
                          <label htmlFor="interventionType">{getMessage("intervention.medicament")}</label>
                          <Field
                              label={`${getMessage("intervention.medicament")}`}
                              id="medicamentId"
                              name="medicamentId"
                              component={Select}
                              options={shippableMedicamentOptions}
                              validate={required}
                              fluid
                          />
                        </>
                    }

                    {
                        (isCovidTest || isVaccination) &&
                        <>
                          <label htmlFor="externalCode">{getMessage("intervention.externalCode")}</label>
                          <Field
                              label={`${getMessage("intervention.externalCode")}`}
                              id="externalCode"
                              name="externalCode"
                              component={Select}
                              options={externalCodeOptions}
                              validate={required}
                              fluid
                          />
                        </>
                    }

                    <label htmlFor="questionSetId">{getMessage("intervention.questionSet")}</label>
                    <Field
                        fluid
                        id="questionSetId"
                        name="questionSetId"
                        component={Select}
                        options={questionSetOptions}
                        clearable
                        searchable
                    />
                    {isClinicalQuestions &&
                        <>
                          <label htmlFor="additionalQuestionSetId">
                            {getMessage("intervention.additionalQuestionSet")}
                          </label>
                          <Field
                              fluid
                              id="additionalQuestionSetId"
                              name="additionalQuestionSetId"
                              component={Select}
                              options={questionSetOptions}
                              searchable
                              validate={required}
                          />
                        </>
                    }

                    <label htmlFor="sampleMethods">{getMessage("intervention.sampleMethods")}</label>
                    <Field
                        label={`${getMessage("intervention.sampleMethods")}`}
                        id="sampleMethods"
                        name="sampleMethods"
                        component={Select}
                        options={sampleMethodOptions}
                        multiple
                        fluid
                    />

                    <label htmlFor="durationInMinutes">{getMessage("intervention.durationInMinutes")}</label>
                    <Field
                        fluid
                        id="durationInMinutes"
                        name="durationInMinutes"
                        component={Input}
                        validate={numberValidator}
                    />

                    <label htmlFor="infoText">{getMessage("intervention.infoText")}</label>
                    <Field
                        fluid
                        id="infoText"
                        name="infoText"
                        component={Input}
                    />
                    <label htmlFor="infoTextEn">{getMessage("intervention.infoTextEn")}</label>
                    <Field
                        fluid
                        id="infoTextEn"
                        name="infoTextEn"
                        component={Input}
                    />
                    <label htmlFor="infoTextFr">{getMessage("intervention.infoTextFr")}</label>
                    <Field
                        fluid
                        id="infoTextFr"
                        name="infoTextFr"
                        component={Input}
                    />
                    <label htmlFor="infoTextIt">{getMessage("intervention.infoTextIt")}</label>
                    <Field
                        fluid
                        id="infoTextIt"
                        name="infoTextIt"
                        component={Input}
                    />

                    <Field
                        label={`${getMessage("intervention.authorityNotificationRequired")}`}
                        id="authorityNotificationRequired"
                        name="authorityNotificationRequired"
                        component={CheckBox}
                    />
                    <Field
                        label={`${getMessage("intervention.suppressPatientEmail")}`}
                        id="suppressPatientEmail"
                        name="suppressPatientEmail"
                        component={CheckBox}
                    />

                    <div className={"divider"}></div>
                    {
                        values.type === InterventionType.DEFAULT &&
                        <>
                          <label htmlFor="blockProcedureIds">{getMessage("intervention.blockProcedures")}</label>
                          <Field
                              label={`${getMessage("intervention.blockProcedures")}`}
                              name="blockProcedureIds"
                              component={Select}
                              options={blockProcedureOptions}
                              validate={oneBlockProcedurePerTariff(tariffs, blockProcedureMap)}
                              fluid
                              search
                              multiple
                          />
                        </>
                    }
                  </Grid.Column>

                  <Grid.Column width={8}>

                    {
                        values.type === InterventionType.DEFAULT &&
                        <Table basic="very" celled collapsing compact>
                          <Table.Header>
                            <Table.Row>
                              <Table.HeaderCell>{getMessage('tariff')}</Table.HeaderCell>
                              <Table.HeaderCell>{getMessage('blockProcedures')}</Table.HeaderCell>
                            </Table.Row>
                          </Table.Header>

                          <Table.Body>
                            {
                              tariffs.map(tariff => {
                                const blockProcSupportedByTariff =
                                    selectedBlockProcedures.filter(bp => bp.tariffs.includes(tariff.id));

                                const tariffErrorClass = blockProcSupportedByTariff.length > 1 ? "tariff-error" : "";

                                return <Table.Row key={`${tariff.id}`}>
                                  <Table.Cell className={`${tariffErrorClass}`}>
                                    {tariff.description}
                                  </Table.Cell>
                                  <Table.Cell className={`tariff-block-procedures ${tariffErrorClass}`}>
                                    {blockProcSupportedByTariff.map((bp, ix) =>
                                        <Fragment key={bp.id}>{ix > 0 ? ", " : ""}
                                          <span>{bp.description}</span></Fragment>
                                    )}
                                  </Table.Cell>
                                </Table.Row>;
                              })
                            }
                          </Table.Body>
                        </Table>
                    }

                    {
                        values.externalCode === InterventionExternalCode.ORDER_ITEM_TEST &&
                        <>
                          <label htmlFor="description">{getMessage("labTestPanel.code")}</label>
                          <Field
                              fluid
                              id="orderItem.code"
                              name="orderItem.code"
                              component={CamelCaseCodeInput}
                              validate={required}
                          />

                          <label htmlFor="description">{getMessage("labTestPanel.name")}</label>
                          <Field
                              fluid
                              id="orderItem.name"
                              name="orderItem.name"
                              component={Input}
                              validate={required}
                          />

                          <label htmlFor="description">{getMessage("labTestPanel.keyPrefix")}</label>
                          <Field
                              fluid
                              id="orderItem.keyPrefix"
                              name="orderItem.keyPrefix"
                              validate={required}
                              component={Input}
                          />

                          <label htmlFor="description">{getMessage("labTestPanel.reportNamePrefix")}</label>
                          <Field
                              fluid
                              id="orderItem.reportNamePrefix"
                              name="orderItem.reportNamePrefix"
                              component={Input}
                          />

                          <label htmlFor="description">{getMessage("labTestPanel.reportType")}</label>
                          <Field
                              fluid
                              id="orderItem.reportType"
                              name="orderItem.reportType"
                              component={Input}
                          />

                          <label htmlFor="description">{getMessage("interventionOrderItem.panels")}</label>
                          <Field
                              fluid
                              id="orderItem.panelIds"
                              name="orderItem.panelIds"
                              component={Select}
                              multiple
                              options={labTestPanelOptions}
                              validate={required}
                          />
                        </>
                    }

                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
              <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
            </UpsertInterventionFormContent>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

}

export default withMessages(
    withInterventionUpsertRefDataContext(
        withAuthContext(UpsertInterventionForm)));
